(() => {
  const namespaceKey = 'GuruShopProductThumbnailRail';
  const sourceScript = document.currentScript;
  const requestedRootId = sourceScript?.dataset.productThumbnailRailRoot;
  const existingApi = window[namespaceKey];

  if (existingApi) {
    existingApi.initById(requestedRootId);
    return;
  }

  const initializedRoots = new WeakSet();

  const desktopQuery = window.matchMedia('(min-width: 1000px)');

  const resolveActiveMedia = (carousel, thumbnailScroller) => {
    const activeThumbnail = thumbnailScroller.querySelector('.product-gallery__thumbnail[aria-current="true"]');

    if (activeThumbnail) {
      const mediaId = activeThumbnail.getAttribute('data-media-id');

      if (mediaId) {
        const escapedMediaId = window.CSS && typeof window.CSS.escape === 'function'
          ? window.CSS.escape(mediaId)
          : mediaId.replace(/"/g, '\\"');
        const selectedMedia = carousel.querySelector(`.product-gallery__media[data-media-id="${escapedMediaId}"]:not([hidden])`);

        if (selectedMedia) {
          return selectedMedia;
        }
      }
    }

    return (
      carousel.querySelector('.product-gallery__media.is-initial:not([hidden])') ||
      carousel.querySelector('.product-gallery__media:not([hidden])')
    );
  };

  const bindThumbnailRail = (gallery) => {
    if (!gallery || gallery.dataset.thumbnailRailBound === 'true') {
      return;
    }

    const thumbnailList = gallery.querySelector('.product-gallery__thumbnail-list');
    const thumbnailScroller = thumbnailList?.querySelector('.product-gallery__thumbnail-scroller');
    const carousel = gallery.querySelector('.product-gallery__carousel');

    if (!thumbnailList || !thumbnailScroller || !carousel) {
      return;
    }

    gallery.dataset.thumbnailRailBound = 'true';
    let rafId = 0;

    const updateRailState = () => {
      if (!desktopQuery.matches) {
        gallery.style.removeProperty('--product-gallery-thumbnail-list-max-height');
        thumbnailList.removeAttribute('data-thumbnail-overflow');
        thumbnailList.removeAttribute('data-thumbnail-not-at-top');
        thumbnailList.removeAttribute('data-thumbnail-at-bottom');
        return;
      }

      const activeMedia = resolveActiveMedia(carousel, thumbnailScroller);
      const mediaHeight = activeMedia ? Math.round(activeMedia.getBoundingClientRect().height) : 0;

      if (mediaHeight > 0) {
        gallery.style.setProperty('--product-gallery-thumbnail-list-max-height', `${mediaHeight}px`);
      }

      const hasOverflow = thumbnailScroller.scrollHeight - thumbnailScroller.clientHeight > 2;
      thumbnailList.toggleAttribute('data-thumbnail-overflow', hasOverflow);

      if (!hasOverflow) {
        thumbnailList.removeAttribute('data-thumbnail-not-at-top');
        thumbnailList.removeAttribute('data-thumbnail-at-bottom');
        return;
      }

      const scrollTop = Math.max(0, thumbnailScroller.scrollTop);
      const atTop = scrollTop <= 1;
      const atBottom = scrollTop + thumbnailScroller.clientHeight >= thumbnailScroller.scrollHeight - 1;

      thumbnailList.toggleAttribute('data-thumbnail-not-at-top', !atTop);
      thumbnailList.toggleAttribute('data-thumbnail-at-bottom', atBottom);
    };

    const queueUpdate = () => {
      if (rafId) {
        return;
      }

      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        updateRailState();
      });
    };

    const resizeObserver = new ResizeObserver(queueUpdate);
    resizeObserver.observe(carousel);
    resizeObserver.observe(thumbnailScroller);

    const mutationObserver = new MutationObserver(queueUpdate);
    mutationObserver.observe(carousel, { subtree: true, attributes: true, attributeFilter: ['hidden', 'class'] });
    mutationObserver.observe(thumbnailScroller, { subtree: true, attributes: true, attributeFilter: ['aria-current', 'hidden'] });

    thumbnailScroller.addEventListener('scroll', queueUpdate, { passive: true });
    carousel.addEventListener('carousel:change', queueUpdate);
    carousel.addEventListener('carousel:settle', queueUpdate);

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', queueUpdate);
    } else if (desktopQuery.addListener) {
      desktopQuery.addListener(queueUpdate);
    }

    gallery.addEventListener('lightbox:open', queueUpdate);
    queueUpdate();

    gallery.__thumbnailRailCleanup = () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }

      resizeObserver.disconnect();
      mutationObserver.disconnect();
      thumbnailScroller.removeEventListener('scroll', queueUpdate);
      carousel.removeEventListener('carousel:change', queueUpdate);
      carousel.removeEventListener('carousel:settle', queueUpdate);
      gallery.removeEventListener('lightbox:open', queueUpdate);

      if (desktopQuery.removeEventListener) {
        desktopQuery.removeEventListener('change', queueUpdate);
      } else if (desktopQuery.removeListener) {
        desktopQuery.removeListener(queueUpdate);
      }
      delete gallery.__thumbnailRailCleanup;
      delete gallery.dataset.thumbnailRailBound;
    };
  };

  const initThumbnailRails = (root) => {
    root.querySelectorAll('product-gallery').forEach(bindThumbnailRail);
  };

  const initRoot = (sectionRoot) => {
    if (!(sectionRoot instanceof HTMLElement) || initializedRoots.has(sectionRoot)) return false;

    initializedRoots.add(sectionRoot);
    initThumbnailRails(sectionRoot);
    return true;
  };

  const initById = (rootId) => {
    if (!rootId) return false;
    return initRoot(document.getElementById(rootId));
  };

  const cleanupRoot = (sectionRoot) => {
    if (!(sectionRoot instanceof HTMLElement) || !initializedRoots.has(sectionRoot)) return false;

    sectionRoot.querySelectorAll('product-gallery').forEach((gallery) => {
      if (typeof gallery.__thumbnailRailCleanup === 'function') {
        gallery.__thumbnailRailCleanup();
      }
    });

    initializedRoots.delete(sectionRoot);
    return true;
  };

  const initWithin = (scope) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;

    const markerSelector = 'script[data-product-thumbnail-rail-root]';

    if (scope.matches?.(markerSelector)) {
      initById(scope.dataset.productThumbnailRailRoot);
    }

    scope.querySelectorAll(markerSelector).forEach((marker) => {
      initById(marker.dataset.productThumbnailRailRoot);
    });
  };

  window[namespaceKey] = { initById, initWithin, cleanupRoot };

  document.addEventListener('shopify:section:load', (event) => {
    initWithin(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    cleanupRoot(event.target);
  });

  initById(requestedRootId);
})();
